import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { AppContext } from "./AppContext";
import { transactionKey } from "./utils/constants";

export type TransactionType = "income" | "expense";

export interface NormalizableTransaction {
    type: string;
    id: number;
    amount: number;
}

export interface NormalizedEntry {
    amount: number;
    transactionType: TransactionType;
    categoryId: number;
}

interface NormalizationState {
    normalizedIds: Set<string>;
    normalizedAmounts: Record<string, NormalizedEntry>;
}

export interface NormalizationContextValue {
    normalizedIds: Set<string>;
    normalizedAmounts: Record<string, NormalizedEntry>;
    hasNormalization: boolean;
    toggleNormalize: (id: string, amount: number, transactionType: TransactionType, categoryId: number) => void;
    toggleNormalizeCategory: (transactions: NormalizableTransaction[], transactionType: TransactionType, categoryId: number) => void;
    adjustAmount: (id: string, originalAmount: number, adjustedAmount: number, transactionType: TransactionType, categoryId: number) => void;
    resetAll: () => void;
}

const EMPTY_STATE: NormalizationState = { normalizedIds: new Set(), normalizedAmounts: {} };

export const NormalizationContext = createContext<NormalizationContextValue | undefined>(undefined);

export function useNormalization(): NormalizationContextValue {
    const ctx = useContext(NormalizationContext);
    if (!ctx) throw new Error("useNormalization must be used within NormalizationProvider");
    return ctx;
}

export function NormalizationProvider({ children }: { children: ReactNode }) {
    const { selectedYear, selectedMonthId } = useContext(AppContext);
    const [stateByMonth, setStateByMonth] = useState<Record<string, NormalizationState>>({});

    const monthKey = `${selectedYear}-${selectedMonthId}`;

    const current = stateByMonth[monthKey] || EMPTY_STATE;
    const normalizedIds = current.normalizedIds;
    const normalizedAmounts = current.normalizedAmounts;
    const hasNormalization = normalizedIds.size > 0;

    const updateMonth = useCallback((updater: (state: NormalizationState) => NormalizationState) => {
        setStateByMonth((prev) => {
            const key = `${selectedYear}-${selectedMonthId}`;
            const current = prev[key] || EMPTY_STATE;
            const next = updater(current);
            return { ...prev, [key]: next };
        });
    }, [selectedYear, selectedMonthId]);

    const toggleNormalize = useCallback((id: string, amount: number, transactionType: TransactionType, categoryId: number) => {
        updateMonth(({ normalizedIds, normalizedAmounts }) => {
            const nextIds = new Set(normalizedIds);
            const nextAmounts = { ...normalizedAmounts };
            if (nextIds.has(id)) {
                nextIds.delete(id);
                delete nextAmounts[id];
            } else {
                nextIds.add(id);
                nextAmounts[id] = { amount, transactionType, categoryId };
            }
            return { normalizedIds: nextIds, normalizedAmounts: nextAmounts };
        });
    }, [updateMonth]);

    const toggleNormalizeCategory = useCallback((transactions: NormalizableTransaction[], transactionType: TransactionType, categoryId: number) => {
        updateMonth(({ normalizedIds, normalizedAmounts }) => {
            const allKeys = transactions.map((tx) => transactionKey(tx.type, tx.id));
            const allNormalized = allKeys.every((key) => normalizedIds.has(key));
            const nextIds = new Set(normalizedIds);
            const nextAmounts = { ...normalizedAmounts };

            if (allNormalized) {
                allKeys.forEach((key) => {
                    nextIds.delete(key);
                    delete nextAmounts[key];
                });
            } else {
                allKeys.forEach((key) => nextIds.add(key));
                transactions.forEach((tx) => {
                    nextAmounts[transactionKey(tx.type, tx.id)] = { amount: tx.amount, transactionType, categoryId };
                });
            }

            return { normalizedIds: nextIds, normalizedAmounts: nextAmounts };
        });
    }, [updateMonth]);

    const adjustAmount = useCallback((id: string, originalAmount: number, adjustedAmount: number, transactionType: TransactionType, categoryId: number) => {
        updateMonth(({ normalizedIds, normalizedAmounts }) => {
            const nextIds = new Set(normalizedIds);
            const nextAmounts = { ...normalizedAmounts };

            if (adjustedAmount === originalAmount) {
                nextIds.delete(id);
                delete nextAmounts[id];
            } else {
                nextIds.add(id);
                nextAmounts[id] = { amount: originalAmount - adjustedAmount, transactionType, categoryId };
            }

            return { normalizedIds: nextIds, normalizedAmounts: nextAmounts };
        });
    }, [updateMonth]);

    const resetAll = useCallback(() => {
        updateMonth(() => EMPTY_STATE);
    }, [updateMonth]);

    const value: NormalizationContextValue = {
        normalizedIds,
        normalizedAmounts,
        hasNormalization,
        toggleNormalize,
        toggleNormalizeCategory,
        adjustAmount,
        resetAll,
    };

    return <NormalizationContext.Provider value={value}>{children}</NormalizationContext.Provider>;
}
